import React, { useState } from "react";
import { SparkleIcon as Sparkle } from "@phosphor-icons/react";

export default function InbreedingCard({ coi, onRecompute }) {
  const pct = coi.percent ?? 0;
  const [recomputing, setRecomputing] = useState(false);
  const run = async (opts) => {
    setRecomputing(true);
    await onRecompute(opts);
    setRecomputing(false);
  };
  // Standard breeder guidance thresholds
  let tone;
  let label;
  if (coi.note || coi.common_ancestors?.length === 0) {
    tone = "text-[#6B6B6B]";
    label = "No common ancestors found in the recorded pedigree.";
  } else if (pct < 6.25) {
    tone = "text-[#0E0E0E]";
    label = "Low — considered safe by most breeding guidelines.";
  } else if (pct < 12.5) {
    tone = "text-[#C1272D]";
    label = "Moderate — equivalent to first-cousin breeding. Monitor health.";
  } else {
    tone = "text-[#C1272D]";
    label = "High — risk of recessive traits. Consult a geneticist.";
  }
  return (
    <div
      className="bg-white border border-[#E5E5E5] rounded-xl p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
      data-testid="inbreeding-card"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkle size={18} weight="duotone" className="text-crimson" />
          <h3 className="font-spectral text-xl">Inbreeding coefficient</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
            {coi.recursive ? "Wright + f_A" : "Wright"} · {coi.max_generations} gen
          </span>
        </div>
      </div>
      <div className="flex items-baseline gap-3">
        <span className={`font-spectral text-5xl tracking-tight ${tone}`} data-testid="coi-percent">
          {pct.toFixed(2)}%
        </span>
        <span className="text-[#6B6B6B] text-sm">COI ({coi.coefficient})</span>
      </div>
      <p className="text-sm text-[#6B6B6B] mt-3">{label}</p>

      <div className="mt-5 flex flex-wrap gap-2 items-center">
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Depth</span>
        {[3, 5, 8, 10].map((g) => (
          <button
            key={g}
            onClick={() => run({ generations: g, recursive: coi.recursive ? "true" : "false" })}
            disabled={recomputing}
            className={`text-xs rounded-full px-3 py-1 border transition-colors ${
              coi.max_generations === g
                ? "bg-[#0E0E0E] text-white border-[#0E0E0E]"
                : "bg-white text-[#6B6B6B] border-[#E5E5E5] hover:border-[#0E0E0E]/40"
            }`}
            data-testid={`coi-gen-${g}`}
          >
            {g}
          </button>
        ))}
        <button
          onClick={() =>
            run({
              generations: coi.max_generations,
              recursive: coi.recursive ? "false" : "true",
            })
          }
          disabled={recomputing}
          className={`ml-auto text-xs rounded-full px-3 py-1 border transition-colors flex items-center gap-1 ${
            coi.recursive
              ? "bg-[#C1272D]/10 text-[#C1272D] border-[#C1272D]/40"
              : "bg-white text-[#6B6B6B] border-[#E5E5E5] hover:border-[#C1272D]/40"
          }`}
          data-testid="coi-toggle-recursive"
          title="Include ancestors' own inbreeding (Wright's full formula)"
        >
          <Sparkle size={12} weight={coi.recursive ? "fill" : "regular"} />
          {coi.recursive ? "Deep analysis on" : "Deep analysis"}
        </button>
      </div>

      {coi.common_ancestors?.length > 0 && (
        <div className="mt-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">
            Common ancestors ({coi.common_ancestors.length})
          </p>
          <ul className="divide-y divide-[#E5E5E5]">
            {coi.common_ancestors.slice(0, 6).map((a) => (
              <li key={a.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{a.name}</div>
                  <div className="text-xs text-[#6B6B6B]">
                    {a.breed || "—"}
                    {a.registration_number ? ` · ${a.registration_number}` : ""}
                    {coi.recursive && a.ancestor_coi > 0 && (
                      <span className="ml-1 text-crimson">
                        · f_A {(a.ancestor_coi * 100).toFixed(2)}%
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-xs font-mono text-crimson flex-shrink-0">
                  +{(a.contribution * 100).toFixed(2)}%
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
